/**
 * wavEncoder — Float32 microphone samples become a WAV the transcriber accepts.
 *
 * WHY WE ENCODE OURSELVES rather than using MediaRecorder. Dictation sends a
 * piece of speech per pause, and MediaRecorder's WebM chunks are NOT
 * independently decodable — only the first carries the headers, so every later
 * piece would be unplayable on its own. A WAV we write ourselves is 44 bytes of
 * header plus samples, so every segment stands alone. That single fact is what
 * makes pause-by-pause dictation possible at all.
 *
 * It also settles the format question: the platform's transcriber declares
 * audio/wav first-party, so nothing has to be transcoded on the way, and Safari
 * — which produces MP4 rather than WebM from MediaRecorder — needs no special
 * case here.
 *
 * 16 kHz mono is deliberate: it is what speech recognition wants, and it is a
 * third of the bytes of 48 kHz, which is a third of the upload on a phone.
 *
 * Pure and IO-free.
 */

/** What the transcriber is happiest with, and a third of the bytes of 48 kHz. */
export const DICTATION_SAMPLE_RATE = 16_000;

/**
 * Average down to the target rate.
 *
 * Averaging the samples in each window rather than picking one of them: point
 * sampling a 48 kHz signal down to 16 kHz aliases, which sounds like a lisp to
 * a recognizer and costs accuracy on exactly the consonants that distinguish
 * words. The mean is a crude low-pass filter, and crude is enough here.
 */
export function downsampleTo16k(input: Float32Array, inputRate: number): Float32Array {
  if (!(inputRate > 0) || input.length === 0) return new Float32Array(0);
  if (inputRate === DICTATION_SAMPLE_RATE) return input.slice();
  const ratio = inputRate / DICTATION_SAMPLE_RATE;
  if (ratio < 1) return input.slice(); // already at or below target — leave it alone
  const outLength = Math.floor(input.length / ratio);
  const out = new Float32Array(outLength);
  for (let i = 0; i < outLength; i += 1) {
    const from = Math.floor(i * ratio);
    const to = Math.min(input.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    for (let j = from; j < to; j += 1) sum += input[j];
    out[i] = to > from ? sum / (to - from) : 0;
  }
  return out;
}

/** Concatenate the frames a segment collected. */
export function concatFrames(frames: readonly Float32Array[]): Float32Array {
  let total = 0;
  for (const f of frames) total += f.length;
  const out = new Float32Array(total);
  let at = 0;
  for (const f of frames) {
    out.set(f, at);
    at += f.length;
  }
  return out;
}

/** How long a run of samples lasts, in milliseconds. */
export function durationMs(sampleCount: number, sampleRate = DICTATION_SAMPLE_RATE): number {
  return sampleRate > 0 ? Math.round((sampleCount / sampleRate) * 1000) : 0;
}

/**
 * A complete 16-bit mono PCM WAV: 44-byte canonical header, then the samples.
 *
 * Clamped before conversion — a sample outside [-1, 1] would wrap around and
 * become a loud click, which a recognizer hears as a consonant that was not
 * said.
 */
export function encodeWav(samples: Float32Array, sampleRate = DICTATION_SAMPLE_RATE): Uint8Array {
  const bytes = new Uint8Array(44 + samples.length * 2);
  const view = new DataView(bytes.buffer);
  const ascii = (at: number, text: string) => {
    for (let i = 0; i < text.length; i += 1) view.setUint8(at + i, text.charCodeAt(i));
  };

  ascii(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true); // file size minus the first 8 bytes
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true);          // fmt chunk size
  view.setUint16(20, 1, true);           // PCM
  view.setUint16(22, 1, true);           // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate (mono, 16-bit)
  view.setUint16(32, 2, true);           // block align
  view.setUint16(34, 16, true);          // bits per sample
  ascii(36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let at = 44;
  for (let i = 0; i < samples.length; i += 1) {
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(at, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
    at += 2;
  }
  return bytes;
}

/** Base64 for the JSON body, chunked so a long segment cannot blow the stack. */
export function toBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let out = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    out += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return typeof btoa === 'function' ? btoa(out) : Buffer.from(bytes).toString('base64');
}
